import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { jsonDownload, authenticatedDownload } from "@/lib/download";
import { useAuth } from "@/lib/auth";
import { usePreferences } from "@/lib/preferences";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Download, KeyRound, LogOut, Trash2, ShieldOff, Sparkles, RotateCcw, FileText, ArrowRight } from "lucide-react";

function Section({ title, children, testid, hidden }) {
  if (hidden) return null;
  return (
    <section
      data-testid={testid}
      className="rounded-lg border border-border-subtle bg-bg-surface p-6"
    >
      <h2 className="text-lg font-heading font-medium text-fg-primary">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function PreferenceRow({ testid, label, detail, value, onChange }) {
  return (
    <div
      className="flex items-start justify-between gap-4 py-3 border-b border-border-subtle last:border-b-0"
      data-testid={testid}
    >
      <div>
        <p className="text-base text-fg-primary">{label}</p>
        <p className="text-xs text-fg-secondary mt-1">{detail}</p>
      </div>
      <Switch
        checked={!!value}
        onCheckedChange={onChange}
        aria-label={label}
        data-testid={`${testid}-switch`}
      />
    </div>
  );
}

export default function Settings() {
  const { user, logoutAll } = useAuth();
  const { prefs, update: updatePrefs } = usePreferences();
  const navigate = useNavigate();
  const [consents, setConsents] = useState([]);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delForm, setDelForm] = useState({ password: "", confirmation: "" });
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [resumeStudio, setResumeStudio] = useState(null);
  const [resumeStudioErr, setResumeStudioErr] = useState(null);
  const [access, setAccess] = useState(null);
  const [trainingSub, setTrainingSub] = useState(null);        // {active, subscription, schedule} | null
  const [cancelTrainingBusy, setCancelTrainingBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/consents");
        setConsents(data.consents || []);
      } catch (_) { /* ignore */ }
    })();
    // Resume Studio settings block: shows the user's resumes with
    // direct links to open / preview / snapshots. Silently degrades
    // if the endpoint is inaccessible (e.g., no consent yet, restricted
    // plan) — the block simply hides its list.
    (async () => {
      try {
        const [{ data: st }, { data: rl }] = await Promise.all([
          api.get("/resume-studio/status"),
          api.get("/resume-studio/resumes"),
        ]);
        setResumeStudio({ status: st, resumes: rl.resumes || [] });
      } catch (err) {
        const code = err?.response?.status;
        // 503 = feature-flag disabled (launch-scope 2026-09-23). Hide
        // the "Your resumes" section entirely — no fake "coming soon"
        // copy in Settings.
        if (code === 503) {
          setResumeStudioErr("__hidden__");
        } else if (code === 402) {
          setResumeStudioErr("Resume Studio isn't included on your current plan.");
        } else {
          setResumeStudioErr(null);
        }
      }
    })();
    // "Your access" section — fetches from /me/access. Silently
    // degrades if unauthorized (participant hits this before login).
    (async () => {
      try {
        const { data } = await api.get("/me/access");
        setAccess(data);
      } catch (_) { /* silent */ }
    })();
    // 2026-10 AI Training subscription block: fetches /training/subscription.
    // 503 → feature flag off; {active:false} → user has no training sub.
    // Both cases silently hide the block (no fake "coming soon" copy).
    (async () => {
      try {
        const { data } = await api.get("/training/subscription");
        setTrainingSub(data || null);
      } catch (_) { /* silent — flag off or endpoint unavailable */ }
    })();
  }, []);

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwErr(null);
    if (pw.next !== pw.confirm) {
      setPwErr("New passwords do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      setPw({ current: "", next: "", confirm: "" });
      // Backend bumped token_version and invalidated our JWT. Clear locally
      // so the SPA doesn't 401 on next request, then send the user to /login.
      const { setToken } = await import("@/lib/api");
      setToken(null);
      toast.success("Password updated. Please log in again.");
      setTimeout(() => navigate("/login", { replace: true }), 700);
    } catch (err) {
      setPwErr(extractApiError(err, "Password change failed."));
    } finally {
      setPwSaving(false);
    }
  };

  const onLogoutAll = async () => {
    try {
      await logoutAll();
      toast.success("All sessions signed out.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(extractApiError(err, "Sign-out failed."));
    }
  };

  const onExport = async () => {
    try {
      const filename = `cra3d-export-${user.email}-${Date.now()}.json`;
      const size = await jsonDownload("/me/export", filename);
      // Only show success after the file was actually handed to the
      // browser. On failure `jsonDownload` throws and the catch shows
      // an honest error.
      toast.success(
        `Export ready (${(size / 1024).toFixed(1)} KB). ` +
        "Check your Downloads folder if it didn't open."
      );
    } catch (err) {
      toast.error(err?.message || "Export failed.");
    }
  };

  // Founder-pass Finding 3: presentation of the EXISTING Direction
  // Snapshot PDF as the primary readable report. No report-generation
  // logic is changed — same endpoint the Portfolio page uses.
  const onDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      const size = await authenticatedDownload(
        "/pdf/direction-snapshot",
        "cra3d_portfolio_report.pdf",
        { mime: "application/pdf" },
      );
      toast.success(
        `Portfolio PDF ready (${(size / 1024).toFixed(1)} KB). ` +
        "Check your Downloads folder if the file didn't open."
      );
    } catch (err) {
      toast.error(err?.message || "Download failed.");
    } finally {
      setPdfBusy(false);
    }
  };

  const onDelete = async () => {
    setDelErr(null);
    if (delForm.confirmation !== "DELETE") {
      setDelErr('Type "DELETE" exactly to confirm.');
      return;
    }
    setDelBusy(true);
    try {
      await api.post("/me/delete", {
        password: delForm.password,
        confirmation: "DELETE",
      });
      toast.success("Your account has been permanently deleted.");
      setDelOpen(false);
      setTimeout(() => navigate("/", { replace: true }), 400);
    } catch (err) {
      setDelErr(extractApiError(err, "Deletion failed. Please try again."));
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <h1
          data-testid="settings-page-title"
          className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
        >
          Account settings
        </h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Manage your account, sessions, consents, and data.
        </p>

        <div className="mt-8 space-y-6">
          <Section title="Profile" testid="settings-profile">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-fg-secondary">Email</dt>
                <dd data-testid="settings-email" className="text-fg-primary mt-1 break-all">{user?.email}</dd>
              </div>
              <div>
                <dt className="text-fg-secondary">Display name</dt>
                <dd data-testid="settings-display-name" className="text-fg-primary mt-1">{user?.display_name || "—"}</dd>
              </div>
              <div>
                <dt className="text-fg-secondary">Role</dt>
                <dd data-testid="settings-role" className="text-fg-primary mt-1 capitalize">{user?.role}</dd>
              </div>
            </dl>
          </Section>

          <Section title="Change password" testid="settings-change-password">
            <form onSubmit={onChangePassword} className="space-y-4" data-testid="change-password-form">
              {pwErr && (
                <Alert variant="destructive" data-testid="change-password-error">
                  <AlertDescription>{pwErr}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="cur">Current password</Label>
                <Input id="cur" type="password" required data-testid="change-password-current"
                  value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
                  className="bg-bg-base border-border-subtle text-fg-primary" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="next">New password</Label>
                  <Input id="next" type="password" required minLength={8} data-testid="change-password-next"
                    value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
                    className="bg-bg-base border-border-subtle text-fg-primary" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="conf">Confirm new password</Label>
                  <Input id="conf" type="password" required minLength={8} data-testid="change-password-confirm"
                    value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                    className="bg-bg-base border-border-subtle text-fg-primary" />
                </div>
              </div>
              <Button type="submit" disabled={pwSaving} data-testid="change-password-submit"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                <KeyRound className="h-4 w-4 mr-2" aria-hidden="true" />
                {pwSaving ? "Updating…" : "Update password"}
              </Button>
            </form>
          </Section>

          <Section title="Sessions" testid="settings-sessions">
            <p className="text-sm text-fg-secondary">
              Log out of every device that currently has your account signed in.
              You will need to sign in again on this device too.
            </p>
            <Button
              variant="outline"
              onClick={onLogoutAll}
              data-testid="logout-all-btn"
              className="mt-4 border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
              Log out of all sessions
            </Button>
          </Section>

          <Section title="Consents on file" testid="settings-consents">
            {consents.length === 0 ? (
              <p className="text-sm text-fg-secondary">No consents recorded.</p>
            ) : (
              <ul data-testid="consents-list" className="space-y-2 text-sm">
                {consents.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`consent-row-${c.policy_type}-${c.version}`}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between border border-border-subtle rounded-md p-3 bg-bg-base"
                  >
                    <span className="text-fg-primary capitalize">
                      {c.policy_type.replace("_", " ")} · v{c.version}
                    </span>
                    <span className="text-fg-secondary text-xs">
                      Consented {new Date(c.consented_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Accessibility & learning preferences" testid="settings-preferences">
            <p className="text-xs text-fg-secondary mb-4">
              Applied immediately.
            </p>
            <PreferenceRow
              testid="pref-plain-language"
              label="Plain-language text"
              detail="When a simpler version of copy is available, prefer it."
              value={!!prefs?.plain_language}
              onChange={(v) => updatePrefs({ plain_language: v })}
            />
            {/* Voice & reading affordance block — surfaces precisely
                how to use the two voice features so the CRA3D
                assessment flow is predictable before a learner clicks
                "start". The anchor id matches the inline help link on
                the assessment page (`/settings#voice`). */}
            <div
              id="voice"
              data-testid="settings-voice-help"
              className="my-4 rounded-md border border-border-subtle bg-bg-surface p-4 text-sm text-fg-primary"
            >
              <p className="font-medium">Voice &amp; reading</p>
              <p className="mt-1 text-xs text-fg-secondary">
                Two independent features, both opt-in. Browser support
                varies &mdash; both features rely on your browser's
                built-in Web Speech API, not a CRA3D server.
              </p>
              <ul className="mt-2 list-disc pl-5 text-xs text-fg-primary space-y-1">
                <li>
                  <strong>Read-aloud (question text spoken by your
                  browser):</strong> flip the toggle below. A speaker
                  icon then appears on each question &mdash; press it
                  to hear the question in your browser's default
                  voice. Nothing is sent to CRA3D. No microphone is
                  used.
                </li>
                <li>
                  <strong>Microphone answering (speak your written
                  answer):</strong> on any free-text question, press
                  the microphone button. Your browser will ask once
                  for microphone permission; grant it. Speak your
                  answer; the browser transcribes it into the text
                  box, which you can edit before saving. Audio is not
                  stored or uploaded.
                </li>
              </ul>
              <p className="mt-2 text-xs text-fg-muted">
                Verified in preview on <strong>Chromium 128 /
                Chrome 128 on Linux</strong>. Firefox and some mobile
                browsers may lack microphone-answering support &mdash;
                the button simply does nothing in those environments.
                Report problems with the exact browser, OS, device,
                and action you were taking.
              </p>
            </div>
            <PreferenceRow
              testid="pref-read-aloud"
              label="Read-aloud (browser TTS)"
              detail="Show device / browser-generated speech controls."
              value={!!prefs?.read_aloud}
              onChange={(v) => updatePrefs({ read_aloud: v })}
            />
            <PreferenceRow
              testid="pref-larger-text"
              label="Larger text"
              detail="Bump the whole app's font size a step up."
              value={!!prefs?.larger_text}
              onChange={(v) => updatePrefs({ larger_text: v })}
            />
            <PreferenceRow
              testid="pref-high-contrast"
              label="High contrast"
              detail="Higher-contrast palette across the app."
              value={!!prefs?.high_contrast}
              onChange={(v) => updatePrefs({ high_contrast: v })}
            />
            <PreferenceRow
              testid="pref-reduced-motion"
              label="Reduced motion"
              detail="Kill non-essential transitions. Respects your OS setting regardless."
              value={!!prefs?.reduced_motion}
              onChange={(v) => updatePrefs({ reduced_motion: v })}
            />
            <p className="text-xs text-fg-secondary mt-6 mb-2">
              Saved for upcoming course features (not yet applied):
            </p>
            <PreferenceRow
              testid="pref-examples-first"
              label="Examples first"
              detail="Applied in upcoming course features."
              value={!!prefs?.examples_first}
              onChange={(v) => updatePrefs({ examples_first: v })}
            />
            <PreferenceRow
              testid="pref-step-by-step"
              label="Step-by-step guidance"
              detail="Applied in upcoming course features."
              value={!!prefs?.step_by_step}
              onChange={(v) => updatePrefs({ step_by_step: v })}
            />
            <PreferenceRow
              testid="pref-practice-first"
              label="Practice-first learning"
              detail="Applied in upcoming course features."
              value={!!prefs?.practice_first}
              onChange={(v) => updatePrefs({ practice_first: v })}
            />
            <PreferenceRow
              testid="pref-short-lessons"
              label="Short lesson preference"
              detail="Applied in upcoming course features."
              value={!!prefs?.short_lessons}
              onChange={(v) => updatePrefs({ short_lessons: v })}
            />
            <div className="mt-4">
              <Link to="/onboarding?revisit=1">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="settings-revisit-onboarding"
                  className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                >
                  <RotateCcw className="h-4 w-4 mr-2" aria-hidden="true" />
                  Revisit onboarding
                </Button>
              </Link>
            </div>
          </Section>

          <Section title="Your access" testid="settings-access">
            <p className="text-sm text-fg-secondary mb-4">
              What's currently included on your account. Purchasing isn't
              open yet in this private development phase — administrators
              can grant test access on request.
            </p>
            {!access ? (
              <p className="text-sm text-fg-secondary"
                 data-testid="settings-access-loading">
                Loading your access…
              </p>
            ) : (
              <>
                <p
                  className="text-sm mb-4"
                  data-testid="settings-access-payments-status"
                >
                  <span className="text-fg-secondary">Payments status: </span>
                  <span className="text-fg-primary">
                    {access.payments_mode === "disabled"
                      ? "Temporarily unavailable — no purchases can be made right now."
                      : access.payments_mode === "test"
                      ? "Test mode — no real charges. Purchases here do not bill a real card."
                      : access.payments_mode === "live"
                      ? "Live — real charges apply."
                      : `Unknown (${access.payments_mode || "—"})`}
                  </span>
                </p>
                <ul className="space-y-2 mb-4"
                    data-testid="settings-access-caps-list">
                  {(access.human_summary || []).map((chip) => (
                    <li
                      key={chip.key}
                      data-testid={`settings-access-cap-${chip.key}`}
                      className="flex items-start gap-2 text-sm"
                    >
                      {chip.included ? (
                        <span
                          className="mt-1 h-1.5 w-1.5 rounded-full bg-progress-teal flex-shrink-0"
                          aria-hidden="true"
                        />
                      ) : (
                        <span
                          className="mt-1 h-1.5 w-1.5 rounded-full bg-fg-muted flex-shrink-0"
                          aria-hidden="true"
                        />
                      )}
                      <span className={chip.included ? "text-fg-primary" : "text-fg-muted"}>
                        {chip.label}
                        {chip.note && (
                          <span className="text-fg-muted"> — {chip.note}</span>
                        )}
                        {!chip.included && (
                          <span className="text-fg-muted italic"> — not included on your current plan</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {(access.active_grants || []).length > 0 && (
                  <div className="mb-4 rounded-md bg-bg-base border border-border-subtle p-3"
                       data-testid="settings-access-grants">
                    <p className="text-xs text-fg-secondary mb-2">
                      Active access grants
                    </p>
                    <ul className="space-y-1.5 text-xs text-fg-primary">
                      {access.active_grants.map((g) => {
                        // Presentational-only source mapping. Purely
                        // reads the `source` field the API already
                        // emits (paywall.py:228). No inference — an
                        // unknown/missing source shows honestly as
                        // "Source not recorded".
                        const src = g.source;
                        let srcLabel = "Source not recorded";
                        let srcTone  = "text-fg-muted";
                        if (src === "admin_test_grant") {
                          srcLabel = "Administrator grant";
                          srcTone  = "text-fg-secondary";
                        } else if (src === "stripe_purchase") {
                          srcLabel = "Stripe purchase";
                          srcTone  = "text-progress-teal";
                        } else if (src === "stripe_subscription") {
                          srcLabel = "Stripe subscription";
                          srcTone  = "text-progress-teal";
                        } else if (src) {
                          srcLabel = "Other source";
                          srcTone  = "text-fg-muted";
                        }
                        return (
                          <li
                            key={g.grant_id}
                            data-testid={`settings-access-grant-${g.product_slug}`}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1"
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-accent-warm flex-shrink-0" aria-hidden="true" />
                            <span className="font-medium">{g.product?.name || g.product_slug}</span>
                            <span
                              data-testid={`settings-access-grant-${g.product_slug}-source`}
                              className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border-subtle ${srcTone}`}
                            >
                              {srcLabel}
                            </span>
                            <span className="text-fg-muted">
                              granted {new Date(g.granted_at).toLocaleDateString()}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {(access.active_grants || []).some((g) => g.source === "stripe_subscription") && (
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="settings-manage-subscription-btn"
                      className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                      onClick={async () => {
                        try {
                          const res = await api.post("/paywall/portal-session", {
                            return_url: `${window.location.origin}/settings`,
                          });
                          if (res.data?.portal_url) {
                            window.location.href = res.data.portal_url;
                          } else {
                            toast.error(
                              "Could not open the subscription management page right now. Please try again.",
                            );
                          }
                        } catch (err) {
                          toast.error(
                            extractApiError(
                              err,
                              "Could not open the subscription management page right now. Please try again.",
                            ),
                          );
                        }
                      }}
                    >
                      Manage subscription
                      <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
                    </Button>
                    <p className="mt-2 text-xs text-fg-secondary">
                      Cancel, change payment method, or view invoices in Stripe's secure portal.
                    </p>
                  </div>
                )}
                {trainingSub?.active && trainingSub.subscription && (
                  <TrainingSubscriptionBlock
                    sub={trainingSub}
                    busy={cancelTrainingBusy}
                    onCancel={async () => {
                      setCancelTrainingBusy(true);
                      try {
                        const res = await api.post(
                          "/training/subscription/cancel",
                          { confirm: true },
                        );
                        toast.success(
                          res.data?.message ||
                          "Your training subscription will stop renewing at the end of the current paid period.",
                        );
                        // Refresh the training subscription view so the
                        // UI reflects cancel_at_period_end=true.
                        try {
                          const r = await api.get("/training/subscription");
                          setTrainingSub(r.data || null);
                        } catch (_) { /* keep local state */ }
                      } catch (err) {
                        toast.error(extractApiError(
                          err,
                          "Could not request cancellation right now. Please try again.",
                        ));
                      } finally {
                        setCancelTrainingBusy(false);
                      }
                    }}
                  />
                )}
                <Link to="/plans">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="settings-access-view-plans-btn"
                    className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                  >
                    See all levels
                    <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
                  </Button>
                </Link>
                <p className="mt-4 text-xs text-fg-secondary">
                  Cancellation, renewal terms and refund conditions are in the{" "}
                  <Link
                    to="/refund-policy"
                    data-testid="settings-access-refund-policy-link"
                    className="underline hover:text-fg-primary"
                  >
                    Refund &amp; Cancellation Policy
                  </Link>.
                </p>
              </>
            )}
          </Section>

          <Section title="Assessment" testid="settings-assessment">
            <p className="text-sm text-fg-secondary mb-4">
              You can start, resume or edit your Career Repath Assessment.
            </p>
            <Link to="/assessment">
              <Button
                data-testid="settings-open-assessment"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
                Open the assessment
              </Button>
            </Link>
          </Section>

          <Section title="Your resumes" testid="settings-resume-studio"
                    hidden={resumeStudioErr === "__hidden__"}>
            <p className="text-sm text-fg-secondary mb-4">
              Manage the resumes you've built in Resume Studio. Each row
              opens the editor. Preview and download live inside the
              editor.
            </p>
            {resumeStudioErr && resumeStudioErr !== "__hidden__" ? (
              <p className="text-sm text-fg-muted italic"
                 data-testid="settings-resume-studio-blocked">
                {resumeStudioErr}
              </p>
            ) : !resumeStudio ? (
              <p className="text-sm text-fg-secondary"
                 data-testid="settings-resume-studio-loading">
                Loading your resumes…
              </p>
            ) : resumeStudio.resumes.length === 0 ? (
              <div className="flex flex-wrap items-center gap-3"
                   data-testid="settings-resume-studio-empty">
                <p className="text-sm text-fg-secondary">
                  You haven't started a resume yet.
                </p>
                <Link to="/resume-studio">
                  <Button
                    size="sm"
                    data-testid="settings-resume-studio-open-btn"
                    className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                  >
                    <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                    Open Resume Studio
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <ul className="space-y-2"
                    data-testid="settings-resume-studio-list">
                  {resumeStudio.resumes.slice(0, 8).map((r) => (
                    <li
                      key={r.id}
                      data-testid={`settings-resume-studio-row-${r.id}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-subtle bg-bg-base p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] uppercase tracking-wider text-fg-secondary">
                          {r.resume_type?.replace(/_/g, " ")}
                          {r.status === "archived" && " · archived"}
                        </p>
                        <p className="mt-0.5 text-sm font-medium text-fg-primary truncate"
                           data-testid={`settings-resume-studio-row-${r.id}-name`}>
                          {r.name}
                        </p>
                        <p className="mt-0.5 text-[10px] text-fg-muted">
                          Updated {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/resume-studio/${r.id}`}>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`settings-resume-studio-row-${r.id}-open-btn`}
                            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                          >
                            Open
                            <ArrowRight className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
                          </Button>
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  <Link to="/resume-studio">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="settings-resume-studio-see-all-btn"
                      className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                    >
                      <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                      Open Resume Studio
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </Section>

          <Section title="Your readable CRA3D report" testid="settings-report-pdf">
            <p className="text-sm text-fg-secondary" data-testid="settings-report-pdf-body">
              Review, save or print a readable summary of your assessment,
              strengths, 3 Lanes of Direction and next steps.
            </p>
            <Button
              onClick={onDownloadPdf}
              disabled={pdfBusy}
              data-testid="settings-download-pdf-btn"
              className="mt-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
              {pdfBusy ? "Preparing PDF…" : "Download Portfolio PDF"}
            </Button>
            <p className="mt-2 text-xs text-fg-muted italic">
              You'll get the same Direction Snapshot PDF that's on your
              Portfolio page. This is a participant progress and work-evidence
              summary — not a certificate, diploma, license, employer
              verification, or a guarantee of employment or income.
            </p>
          </Section>

          <Section title="Your detailed account data" testid="settings-export">
            <p className="text-sm text-fg-secondary" data-testid="settings-export-body">
              Download a technical JSON copy of the information CRA3D stores
              about your account. It may be useful for detailed review, backup
              or transferring your information. For an easier-to-read report,
              use the Portfolio PDF above.
            </p>
            <Button
              variant="outline"
              onClick={onExport}
              data-testid="export-btn"
              className="mt-4 border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <Download className="h-4 w-4 mr-2" aria-hidden="true" />
              Download technical data (.json)
            </Button>
          </Section>

          <section
            data-testid="settings-danger-zone"
            className="rounded-lg border border-destructive/60 bg-destructive/5 p-6"
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-destructive/20 text-destructive">
                <ShieldOff className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-lg font-heading font-medium text-fg-primary">Delete account</h2>
                <p className="mt-2 text-sm leading-relaxed text-fg-secondary">
                  Permanent. Removes your account, consents, and any related records.
                  You cannot recover this. Please export your data first if you want a copy.
                </p>
                <Dialog open={delOpen} onOpenChange={setDelOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="destructive"
                      data-testid="open-delete-dialog"
                      className="mt-4"
                    >
                      <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                      Delete my account
                    </Button>
                  </DialogTrigger>
                  <DialogContent
                    data-testid="delete-dialog"
                    className="bg-bg-surface border-border-subtle text-fg-primary"
                  >
                    <DialogHeader>
                      <DialogTitle>Delete account permanently</DialogTitle>
                      <DialogDescription className="text-fg-secondary">
                        Type <span className="text-fg-primary font-medium">DELETE</span> to confirm and enter your password.
                        This cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {delErr && (
                        <Alert variant="destructive" data-testid="delete-error">
                          <AlertDescription>{delErr}</AlertDescription>
                        </Alert>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="del_conf">Type DELETE</Label>
                        <Input
                          id="del_conf"
                          data-testid="delete-confirmation-input"
                          value={delForm.confirmation}
                          onChange={(e) => setDelForm({ ...delForm, confirmation: e.target.value })}
                          className="bg-bg-base border-border-subtle text-fg-primary"
                          placeholder="DELETE"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="del_pw">Password</Label>
                        <Input
                          id="del_pw"
                          type="password"
                          data-testid="delete-password-input"
                          value={delForm.password}
                          onChange={(e) => setDelForm({ ...delForm, password: e.target.value })}
                          className="bg-bg-base border-border-subtle text-fg-primary"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setDelOpen(false)}
                        data-testid="delete-cancel-btn"
                        className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={onDelete}
                        disabled={delBusy}
                        data-testid="delete-confirm-btn"
                      >
                        {delBusy ? "Deleting…" : "Delete permanently"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}


/**
 * 2026-10 AI Training subscription management block. Shows the plan
 * slug, current period end (unix epoch → local date), cancel-at-
 * period-end state, and the two-phase SubscriptionSchedule disclosure
 * when the user is on the annual membership ($149.99 first year →
 * $169.99 renewal). All amounts are read-only; cancellation hits
 * /api/training/subscription/cancel which Stripe-cancels at period
 * end (never immediate).
 */
function TrainingSubscriptionBlock({ sub, busy, onCancel }) {
  const info = sub.subscription || {};
  const schedule = sub.schedule;
  const periodEndIso = info.current_period_end;
  const periodEndDate = periodEndIso
    ? new Date(
        typeof periodEndIso === "number"
          ? periodEndIso * 1000
          : periodEndIso,
      ).toLocaleDateString()
    : null;
  const cancelled = !!info.cancel_at_period_end;
  const slug = info.product_slug;
  const isAnnual = slug === "training_membership_annual";

  return (
    <div
      data-testid="settings-training-subscription-block"
      data-slug={slug}
      className="mb-4 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-4"
    >
      <p
        data-testid="settings-training-sub-label"
        className="text-[11px] uppercase tracking-widest text-progress-teal"
      >
        AI Training subscription
      </p>
      <p
        data-testid="settings-training-sub-slug"
        className="mt-1 text-sm text-fg-primary font-medium"
      >
        {slug === "training_membership_annual"
          ? "Annual membership"
          : slug}
      </p>
      {periodEndDate && (
        <p
          data-testid="settings-training-sub-period-end"
          className="mt-1 text-xs text-fg-secondary"
        >
          {cancelled ? "Access through " : "Next renewal on "}
          <span className="text-fg-primary">{periodEndDate}</span>
          {cancelled ? " — no further charges." : "."}
        </p>
      )}

      {isAnnual && schedule && Array.isArray(schedule.phases) && (
        <div
          data-testid="settings-training-sub-schedule"
          className="mt-3 rounded border border-border-subtle bg-bg-surface p-3"
        >
          <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
            Stripe SubscriptionSchedule &middot; {schedule.schedule_id}
          </p>
          <ul className="mt-2 space-y-1 text-xs text-fg-primary">
            {schedule.phases.map((ph, i) => (
              <li
                key={i}
                data-testid={`settings-training-sub-phase-${i}`}
                className="flex items-baseline justify-between gap-2"
              >
                <span>
                  Phase {i} &middot;{" "}
                  {ph.iterations ? `${ph.iterations}× ` : "ongoing "}
                  <span className="text-fg-secondary">
                    (
                    {ph.amount_cents != null
                      ? `$${(ph.amount_cents / 100).toFixed(2)}/year`
                      : "amount unknown"}
                    )
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-fg-muted leading-relaxed">
            Both phase amounts are disclosed before purchase. Stripe
            automatically transitions from phase 0 to phase 1 at the
            end of year one.
          </p>
        </div>
      )}

      <div className="mt-3">
        {cancelled ? (
          <p
            data-testid="settings-training-sub-cancelled-note"
            className="text-xs text-fg-primary"
          >
            Cancellation requested — your card will not be charged again.
          </p>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={onCancel}
            data-testid="settings-training-sub-cancel-btn"
            className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
          >
            {busy ? "Cancelling…" : "Cancel AI Training subscription"}
          </Button>
        )}
        <p className="mt-2 text-[11px] text-fg-secondary">
          Cancellation stops the next automatic charge. You keep access
          to the AI Training lessons through the end of the paid period
          you&rsquo;ve already been charged for.
        </p>
      </div>
    </div>
  );
}
